/**
 * ReservationQuickActions - 인라인 승인/거절/상세 버튼
 * Phase 0D2: AppointmentTimeline 직접 사용 (Consult 경유 제거)
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { appointmentApi } from "@/api/appointment";
import { handleApiError } from "@/api/client";
import { useToast } from "@/components/ui/use-toast";
import type { AppointmentTimeline } from "@/types/appointment";

interface ReservationQuickActionsProps {
  appointment: AppointmentTimeline;
  onDetailClick: () => void;
  onPrioritySelect?: (data: AppointmentTimeline) => void;
}

export function ReservationQuickActions({
  appointment,
  onDetailClick,
  onPrioritySelect,
}: ReservationQuickActionsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const appointmentId = appointment.id;
  const status = appointment.status;
  const isRequested = status === "REQUESTED" || status === "CHECKING";

  const hasPriority =
    appointment.firstChoiceDate &&
    appointment.firstChoiceTime &&
    appointment.secondChoiceDate &&
    appointment.secondChoiceTime;

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["appointments-search"] });
    queryClient.invalidateQueries({ queryKey: ["appointments-summary"] });
    queryClient.invalidateQueries({ queryKey: ["appointments-calendar"] });
  };

  const approveMutation = useMutation({
    mutationFn: (priority?: number) =>
      appointmentApi.approve(appointmentId, priority),
    onSuccess: () => {
      toast({ title: "예약이 승인되었습니다." });
      invalidateQueries();
    },
    onError: (error: unknown) => {
      toast({
        title: "승인 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason?: string) =>
      appointmentApi.reject(appointmentId, reason),
    onSuccess: () => {
      toast({ title: "예약이 거절되었습니다." });
      setShowRejectInput(false);
      setRejectReason("");
      invalidateQueries();
    },
    onError: (error: unknown) => {
      toast({
        title: "거절 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!appointmentId) return;

    if (hasPriority && onPrioritySelect) {
      onPrioritySelect(appointment);
    } else {
      approveMutation.mutate(undefined);
    }
  };

  const handleRejectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRejectInput(true);
  };

  const handleRejectSubmit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!appointmentId) return;
    rejectMutation.mutate(rejectReason || undefined);
  };

  const handleRejectCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRejectInput(false);
    setRejectReason("");
  };

  if (!isRequested) {
    return (
      <Button
        size="sm"
        variant="ghost"
        className="h-8 text-xs text-muted-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onDetailClick();
        }}
      >
        상세
        <ChevronRight className="h-3 w-3 ml-0.5" />
      </Button>
    );
  }

  if (showRejectInput) {
    return (
      <div
        className="flex items-center gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          type="text"
          placeholder="거절 사유..."
          className="h-7 text-xs w-[120px]"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRejectSubmit(e as any);
            if (e.key === "Escape") handleRejectCancel(e as any);
          }}
        />
        <Button
          size="sm"
          variant="destructive"
          className="h-7 text-xs px-2"
          onClick={handleRejectSubmit}
          disabled={rejectMutation.isPending}
        >
          확인
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs px-1.5"
          onClick={handleRejectCancel}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="sm"
        className="h-7 text-xs px-2.5 bg-emerald-600 hover:bg-emerald-700"
        onClick={handleApprove}
        disabled={approveMutation.isPending}
      >
        <Check className="h-3 w-3 mr-1" />
        승인
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs px-2.5 border-destructive/50 text-destructive hover:bg-destructive/10"
        onClick={handleRejectClick}
        disabled={rejectMutation.isPending}
      >
        <X className="h-3 w-3 mr-1" />
        거절
      </Button>
    </div>
  );
}
